import React, { useMemo } from 'react';
import { Box, Typography, Avatar, CircularProgress, alpha } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { Person as PersonIcon, Chat as ChatIcon, Search as SearchIcon } from '@mui/icons-material';
import useLastSeen from '../../../hooks/useLastSeen';
import capitalizeEachFirstWord from '../../../utils/CapLetters';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
`;

// Container
const ResultsContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  overflowY: 'auto',
  overflowX: 'hidden',

  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.14)'
      : 'rgba(0,0,0,0.12)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.24)'
      : 'rgba(0,0,0,0.20)',
  },
}));

// Section headers - WhatsApp style
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'capitalize',
  letterSpacing: '0.02em',
  color: theme.palette.mode === 'dark'
    ? alpha('#fff', 0.55)
    : alpha('#000', 0.55),
  padding: '16px 16px 8px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',

  '& .MuiSvgIcon-root': {
    fontSize: '18px',
    opacity: 0.7,
  },
}));

// WhatsApp-style result item
const ResultItem = styled(Box)(({ theme, isActive }) => ({
  cursor: 'pointer',
  padding: '12px 16px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  background: isActive
    ? theme.palette.mode === 'dark'
      ? alpha('#fff', 0.08)
      : alpha('#000', 0.05)
    : 'transparent',
  transition: 'background 0.2s ease',
  animation: `${fadeIn} 0.3s ease-out`,

  '&:hover': {
    background: theme.palette.mode === 'dark'
      ? alpha('#fff', 0.05)
      : alpha('#000', 0.04),
  },

  '&:active': {
    background: theme.palette.mode === 'dark'
      ? alpha('#fff', 0.1)
      : alpha('#000', 0.08),
  },
}));

// Avatar wrapper with online status
const AvatarWrapper = styled(Box)(({ isOnline, theme }) => ({
  position: 'relative',
  flexShrink: 0,

  '&::after': isOnline ? {
    content: '""',
    position: 'absolute',
    bottom: '2px',
    right: '2px',
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    background: '#25D366',
    border: `2px solid ${theme.palette.mode === 'dark' ? '#111' : '#fff'}`,
    animation: `${pulse} 2s ease-in-out infinite`,
  } : {},
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 52,
  height: 52,
  fontSize: '20px',
  fontWeight: 700,
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
}));

// Content area
const UserInfo = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

const Username = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 500,
  color: theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const StatusText = styled(Typography)(({ theme, isOnline }) => ({
  fontSize: '14px',
  fontWeight: 400,
  color: isOnline
    ? '#25D366'
    : theme.palette.mode === 'dark'
    ? alpha('#fff', 0.55)
    : alpha(theme.palette.text.secondary, 0.6),
}));

// Badge for NEW users
const NewBadge = styled(Box)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 600,
  color: '#D44401',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  flexShrink: 0,
}));

// Unread count badge
const UnreadBadge = styled(Box)(({ theme }) => ({
  minWidth: '20px',
  height: '20px',
  borderRadius: '10px',
  background: '#25D366',
  color: '#000',
  fontSize: '12px',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 6px',
  flexShrink: 0,
  boxShadow: '0 1px 3px rgba(37, 211, 102, 0.4)',
}));

// Empty states
const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: '60px 20px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',

  '& .MuiSvgIcon-root': {
    fontSize: '64px',
    color: theme.palette.mode === 'dark'
      ? alpha('#fff', 0.2)
      : alpha('#000', 0.15),
    marginBottom: '8px',
  },
}));

const EmptyText = styled(Typography)(({ theme }) => ({
  fontSize: '15px',
  fontWeight: 500,
  color: theme.palette.mode === 'dark'
    ? alpha('#fff', 0.4)
    : alpha('#000', 0.45),
}));

// Loading state
const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '60px 20px',
  gap: '16px',
}));

const LoadingText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 500,
  color: theme.palette.mode === 'dark'
    ? alpha('#fff', 0.6)
    : alpha('#000', 0.6),
}));

/**
 * SearchResults Component - WhatsApp Style
 * Displays search results with existing chats and new users
 */
const SearchResults = ({
  searchResults,
  searchInProgress,
  onSelectChat,
  onSelectNewUser,
  currentUserId,
  activeChatId,
}) => {
  const { direct_chats = {}, new_users = [] } = searchResults || {};

  const hasDirectChats = Object.keys(direct_chats).length > 0;
  const hasNewUsers = new_users.length > 0;
  const hasResults = hasDirectChats || hasNewUsers;

  if (searchInProgress) {
    return (
      <LoadingContainer>
        <CircularProgress size={40} thickness={4} />
        <LoadingText>Searching...</LoadingText>
      </LoadingContainer>
    );
  }

  if (!hasResults) {
    return (
      <EmptyState>
        <SearchIcon />
        <EmptyText>No results found</EmptyText>
      </EmptyState>
    );
  }

  return (
    <ResultsContainer>
      {/* Existing Direct Chats */}
      {hasDirectChats && (
        <>
          <SectionTitle>
            <ChatIcon />
            Chats
          </SectionTitle>
          {Object.values(direct_chats).map((chat) => {
            const otherUser = chat.room_participants?.find(
              (p) => p.id?.toString() !== currentUserId?.toString()
            );
            if (!otherUser) return null;

            return (
              <ChatResultItem
                key={chat.id}
                chat={chat}
                otherUser={otherUser}
                onClick={() => onSelectChat(chat)}
                isActive={chat.id?.toString() === activeChatId?.toString()}
              />
            );
          })}
        </>
      )}

      {/* New Users (Not yet chatted with) */}
      {hasNewUsers && (
        <>
          <SectionTitle>
            <PersonIcon />
            Other contacts
          </SectionTitle>
          {new_users.map((user) => (
            <NewUserResultItem
              key={user.id}
              user={user}
              onClick={() => onSelectNewUser(user)}
            />
          ))}
        </>
      )}
    </ResultsContainer>
  );
};

// Sub-component for chat result items
const ChatResultItem = ({ chat, otherUser, onClick, isActive }) => {
  const lastSeenText = useLastSeen(otherUser.last_seen, otherUser.is_online);
  const firstLetter = otherUser.username?.[0]?.toUpperCase() || "?";
  const isOnline = otherUser.is_online === 1;
  const unreadCount = chat.unread_count || 0;

  return (
    <ResultItem onClick={onClick} isActive={isActive}>
      <AvatarWrapper isOnline={isOnline}>
        <StyledAvatar>{firstLetter}</StyledAvatar>
      </AvatarWrapper>
      <UserInfo>
        <Username>{capitalizeEachFirstWord(otherUser.username)}</Username>
        <StatusText isOnline={isOnline}>{lastSeenText}</StatusText>
      </UserInfo>
      {unreadCount > 0 && (
        <UnreadBadge>{unreadCount > 99 ? '99+' : unreadCount}</UnreadBadge>
      )}
    </ResultItem>
  );
};

// Sub-component for new user result items
const NewUserResultItem = ({ user, onClick }) => {
  const lastSeenText = useLastSeen(user.last_seen, user.is_online);
  const firstLetter = user.username?.[0]?.toUpperCase() || "?";
  const isOnline = user.is_online === 1;

  return (
    <ResultItem onClick={onClick}>
      <AvatarWrapper isOnline={isOnline}>
        <StyledAvatar>{firstLetter}</StyledAvatar>
      </AvatarWrapper>
      <UserInfo>
        <Username>{capitalizeEachFirstWord(user.username)}</Username>
        <StatusText isOnline={isOnline}>{lastSeenText}</StatusText>
      </UserInfo>
      <NewBadge>NEW</NewBadge>
    </ResultItem>
  );
};

export default SearchResults;
